'use client'

import Script from 'next/script'
import { usePathname } from 'next/navigation'
import { useCountry } from '@/app/country'
import { useEffect, useRef } from 'react'

interface ScriptsProps {
  nonce: string
}

type HubSpotWindow = Window & {
  HubSpotConversations?: {
    widget: {
      refresh: (options?: { openToNewThread?: boolean }) => void
    }
  }
  _hsq?: unknown[][]
  hsConversationsOnReady?: Array<() => void>
}

const syncHubSpotWidget = (pathname: string, localeChanged: boolean) => {
  const hubspotWindow = window as HubSpotWindow
  const _hsq = (hubspotWindow._hsq = hubspotWindow._hsq || [])
  _hsq.push(['setPath', pathname])
  _hsq.push(['trackPageView'])

  hubspotWindow.HubSpotConversations?.widget?.refresh(
    localeChanged ? { openToNewThread: true } : undefined
  )
}

const HubSpotChatManager = () => {
  const pathname = usePathname() ?? ''
  const locale = pathname.split('/')[1]
  const previousLocaleRef = useRef<string | undefined>(undefined)
  const registeredOnReadyRef = useRef(false)
  const latestSyncRef = useRef<() => void>(() => {})

  useEffect(() => {
    const localeChanged =
      previousLocaleRef.current !== undefined &&
      previousLocaleRef.current !== locale
    previousLocaleRef.current = locale

    latestSyncRef.current = () => syncHubSpotWidget(pathname, localeChanged)
    const hubspotWindow = window as HubSpotWindow

    if (hubspotWindow.HubSpotConversations?.widget) {
      latestSyncRef.current()
      return
    }

    hubspotWindow.hsConversationsOnReady =
      hubspotWindow.hsConversationsOnReady || []
    if (!registeredOnReadyRef.current) {
      hubspotWindow.hsConversationsOnReady.push(() => latestSyncRef.current())
      registeredOnReadyRef.current = true
    }
  }, [locale, pathname])

  return null
}

const Scripts: React.FC<ScriptsProps> = ({ nonce }) => {
  const pathname = usePathname()
  const locale = pathname?.split('/')[1] ?? 'fr'
  const region = useCountry()

  const kameleoonKey =
    process.env.NEXT_PUBLIC_REACT_APP_ENVIRONMENT === 'production'
      ? '//pb55kayfq8.kameleoon.eu/engine.js'
      : '//tzlfb51kyr.kameleoon.io/engine.js'

  const googleMapsApiKey = process.env.NEXT_PUBLIC_GOOGLE_API_KEY
  const googleMapsUrl =
    'https://maps.googleapis.com/maps/api/js?' +
    `key=${googleMapsApiKey}&libraries=places&language=${locale}&region=${region}`

  return (
    <>
      {/* GOOGLE MAPS */}
      <Script src={googleMapsUrl} nonce={nonce} />

      {/* GOOGLE ANALYTICS */}
      <Script id='google-analytics'>
        {`(function (i, s, o, g, r, a, m) {
        i['GoogleAnalyticsObject'] = r; i[r] = i[r] || function () {
          (i[r].q = i[r].q || []).push(arguments)
        }, i[r].l = 1 * new Date(); a = s.createElement(o),
          m = s.getElementsByTagName(o)[0]; a.async = 1; a.src = g; m.parentNode.insertBefore(a, m)
      })(window, document, 'script', '//www.google-analytics.com/analytics.js', 'ga');
      ga('create', 'UA-61742288-2', 'auto');
      ga('send', 'pageview');`}
      </Script>

      {/* TRUSTPILOT */}
      <Script
        type='text/javascript'
        src='//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js'
        async
        nonce={nonce}
      />

      {process.env.NEXT_PUBLIC_REACT_APP_ENVIRONMENT !== 'development' && (
        <>
          <Script
            type='text/javascript'
            id='hs-script-loader'
            async
            defer
            src={`//js.hs-scripts.com/${process.env.NEXT_PUBLIC_HUBSPOT_PLATFORM}.js`}
            nonce={nonce}
          />
          <HubSpotChatManager />
        </>
      )}
      {/* KAMELEOON */}
      <>
        <Script
          type='text/javascript'
          src='/scripts/Kameleoon/kameleoon.js'
          async={true}
          nonce={nonce}
        />
        <Script
          type='text/javascript'
          src={kameleoonKey}
          async={true}
          nonce={nonce}
        />
      </>
    </>
  )
}

export default Scripts
